import { downloadFile, getFileNameFromHeader } from '../../../api/reportSettingsApi';
import { TemplatesApi } from '../../../api/templatesApi';
import { BatchDeterminationContext } from '../../../contexts/BatchDeterminationContext';
import { OrganizationContext } from '../../../contexts/OrganizationContext';
import { SortInterface } from '../../../interfaces/ImportHistory';
import { SnackbarParameters } from '../../../interfaces/Snackbar';
import { Template } from '../../../interfaces/Templates';
import withSnackbar from '../../../shared/components/withSnackbar';
import { EMPTY_ARRAY, INITIAL_PAGE_STATE, INITIAL_SORT_COLUMN_ASC } from '../../../shared/constants/constants';
import { customTemplatesTableColumns } from '../../../shared/helpers/savedTemplatesHelpers';
import DeleteTemplateDialog from './DeleteTemplateDialog';
import RenameTemplateDialog from './RenameTemplateDialog';
import {
	SovosCard,
	SovosIconButton,
	SovosLinearProgress,
	SovosMenuItem,
	SovosTable,
	SovosTheme,
	SovosToolbar,
	SovosToolbarActions,
	SovosToolbarBulkActions,
} from 'mosaic-react';
import { Download, Upload } from 'mosaic-react-icons';
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';

const noResultsFoundStyle = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	marginTop: 20,
	marginBottom: 20,
};
interface SaveTemplatesProps {
	showSnackbar: (value: SnackbarParameters) => void;
}

const SavedTemplates: React.FC<SaveTemplatesProps> = (props: SaveTemplatesProps) => {
	const { showSnackbar } = props;
	const { forceRefreshingTemplateTableId } = useContext(BatchDeterminationContext);
	const [customTemplates, setCustomTemplates] = useState<Template[]>(EMPTY_ARRAY);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [sortedColumn, setSortedColumn] = useState<SortInterface>(INITIAL_SORT_COLUMN_ASC);
	const [totalItems, setTotalItems] = useState<number>(INITIAL_PAGE_STATE.totalEntries);
	const [isRenameDialogOpen, setIsRenameDialogOpen] = useState<boolean>(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const { selectedOrg } = useContext(OrganizationContext);
	const [selected, setSelected] = useState<Template[]>([]);
	const [selectedRow, setSelectedRow] = useState<Template>();
	const [isImportingTemplates, setIsImportingTemplates] = useState<boolean>(false);
	const uploadTemplateHiddenInput = useRef<HTMLInputElement>(null);

	const handleRenameTemplate = (template: Template): void => {
		setSelectedRow(template);
		setIsRenameDialogOpen(true);
	};

	const handleDeleteTemplate = (template: Template): void => {
		setSelectedRow(template);
		setIsDeleteDialogOpen(true);
	};

	const getActionMenuItems = (row: Template): JSX.Element[] => [
		<SovosMenuItem key={1} onClick={() => handleRenameTemplate(row)}>
			Rename
		</SovosMenuItem>,
		<SovosMenuItem key={2} onClick={() => handleDeleteTemplate(row)}>
			Delete
		</SovosMenuItem>,
	];

	const isRowSelected = (row: Template): boolean => {
		return !!selected.find(r => r.id === row.id);
	};

	const toggleRowSelect = (row: Template): void => {
		const selectedRow = selected.find(r => r.id === row.id);
		if (selected.length >= 0) {
			if (selectedRow) {
				setSelected(selected.filter(r => r.id !== selectedRow.id));
			} else {
				setSelected([...selected, row]);
			}
		} else {
			setSelected([row]);
		}
	};

	const areAllRowsSelected = (): boolean => {
		return selected.length === customTemplates.length && customTemplates.length !== 0;
	};

	const fetchSavedTemplates = (sortedColumnBy: SortInterface = INITIAL_SORT_COLUMN_ASC): void => {
		setIsLoading(true);
		TemplatesApi.getSavedTemplates(sortedColumnBy)
			.then((response: Template[]) => {
				setCustomTemplates(response);
				setTotalItems(response.length);
				setSelected([]);
			})
			.finally(() => setIsLoading(false));
	};

	const handleColumnSort = ({ id }: { id: number }): void => {
		setSortedColumn(sortedColumn => ({
			...sortedColumn,
			id: id,
			isAscending: sortedColumn.isAscending === null ? true : id === sortedColumn.id ? !sortedColumn.isAscending : true,
		}));
	};

	const handleImportTemplate = (event: ChangeEvent<HTMLInputElement>): void => {
		setIsImportingTemplates(true);
		const fileToUpload = event.target.files ? event.target.files[0] : new Blob();
		const formData = new FormData();
		formData.append('file', fileToUpload);
		TemplatesApi.importTemplates(formData)
			.then(response => {
				if (response.status === 200) {
					showSnackbar({
						message: `${response.data.length} 
			  ${response.data.length > 1 ? 'templates have' : 'template has'} been imported successfully`,
					});
					fetchSavedTemplates();
				}
			})
			.catch(error => {
				switch (error.response.status) {
					case 400:
						if (error.response.data.errors) {
							showSnackbar({
								message: error.response.data.errors[0].message,
								variant: 'error',
							});
						} else {
							showSnackbar({
								message: error.response.data.message,
								variant: 'error',
							});
						}
						break;
					case 403:
						break;
					default:
						showSnackbar({
							message: 'Unable to import templates, please try again',
							variant: 'error',
						});
						break;
				}
			})
			.finally(() => setIsImportingTemplates(false));
	};

	const handleExportSelectedTemplates = (): void => {
		showSnackbar({
			message: `${selected.length} 
		  ${selected.length > 1 ? 'templates are' : 'template is'} being prepared for download`,
		});
		const selectedTemplatesIds = selected.map(template => template.id);
		TemplatesApi.exportTemplates(selectedTemplatesIds)
			.then(response => {
				if (response.status === 200) {
					downloadFile(getFileNameFromHeader(response.headers), new Blob([JSON.stringify(response.data)]));
					showSnackbar({
						message: `${selected.length} 
				  ${selected.length > 1 ? 'templates have' : 'template has'} been downloaded`,
					});
					setSelected([]);
				}
			})
			.catch(error => {
				switch (error.response.status) {
					case 400:
						if (error.response.data.errors) {
							showSnackbar({
								message: error.response.data.errors[0].message,
								variant: 'error',
							});
						} else {
							showSnackbar({
								message: error.response.data.message,
								variant: 'error',
							});
						}
						fetchSavedTemplates();
						setSelected([]);
						break;
					case 403:
						break;
					case 500:
						showSnackbar({
							message: 'Unable to export templates because of a connectivity error. Try again later.',
							variant: 'error',
						});
						break;
					default:
						showSnackbar({
							message: 'Unable to export templates, please try again',
							variant: 'error',
						});
						break;
				}
			});
	};

	const snackbarData = (value: SnackbarParameters): void => {
		if (value.open) {
			showSnackbar({ message: value.message, variant: value.variant });
			fetchSavedTemplates();
		}
	};

	useEffect(() => {
		fetchSavedTemplates(sortedColumn);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortedColumn, selectedOrg, forceRefreshingTemplateTableId]);

	return (
		<>
			<SovosCard style={{ borderRadius: '0px 0px 4px 4px' }}>
				{isLoading || isImportingTemplates ? (
					<SovosLinearProgress />
				) : (
					<>
						<input
							ref={uploadTemplateHiddenInput}
							type='file'
							accept='application/json'
							onChange={handleImportTemplate}
							onClick={event => ((event.target as HTMLInputElement).value = null || '')}
							style={{ display: 'none' }}
						/>
						<SovosToolbar>
							{totalItems >= 0 && (
								<>
									<SovosToolbarBulkActions count={selected?.length}>
										<SovosIconButton onClick={handleExportSelectedTemplates} tooltipText='Export Templates'>
											<Download style={{ color: SovosTheme.palette.primary.contrastText }} />
										</SovosIconButton>
									</SovosToolbarBulkActions>
									<SovosToolbarActions>
										<SovosIconButton
											onClick={() =>
												uploadTemplateHiddenInput.current !== null ? uploadTemplateHiddenInput.current.click() : ''
											}
											tooltipText='Import Templates'
										>
											<Upload />
										</SovosIconButton>
									</SovosToolbarActions>
								</>
							)}
						</SovosToolbar>
						{totalItems > 0 ? (
							<SovosTable
								className='table-content'
								columns={customTemplatesTableColumns}
								data={customTemplates}
								onColumnSort={handleColumnSort}
								columnSortId={sortedColumn.id}
								rowActions={getActionMenuItems}
								onRowSelection={{
									isRowSelected: (row: Template) => isRowSelected(row),
									toggleRowSelection: (row: Template) => toggleRowSelect(row),
									areAllRowsSelected: areAllRowsSelected,
									areAnyRowsSelected: () => !!selected.length,
									toggleSelectAll: () => setSelected(!areAllRowsSelected() ? [...customTemplates] : []),
								}}
							/>
						) : (
							<div style={{ ...noResultsFoundStyle, flexDirection: 'column' }}>
								<h6>No results found</h6>
								<p style={{ fontSize: 14 }}>
									Try adjusting your filters or select a new date range to find what you&apos;re looking for.{' '}
								</p>
							</div>
						)}
						<RenameTemplateDialog
							isDialogOpen={isRenameDialogOpen}
							setIsDialogOpen={setIsRenameDialogOpen}
							template={selectedRow!}
							snackbarData={snackbarData}
						/>
						<DeleteTemplateDialog
							isDialogOpen={isDeleteDialogOpen}
							setIsDialogOpen={setIsDeleteDialogOpen}
							template={selectedRow!}
							snackbarData={snackbarData}
						/>
					</>
				)}
			</SovosCard>
		</>
	);
};

export default withSnackbar(SavedTemplates);
